import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, PageBody, StatusBadge, EmptyState, formatMoney } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Upload, Plus, Trash2, ArrowRightCircle, Download, FileText, FileSpreadsheet, Send } from "lucide-react";
import { toast } from "sonner";
import { PartNumberAutocomplete } from "../components/PartNumberAutocomplete";

const CURRENCIES = ["USD", "EUR", "TRY", "GBP", "JPY", "CNY"];

export default function OfferDetail() {
  const { id } = useParams();
  const [offer, setOffer] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [selected, setSelected] = useState({});
  const [uploading, setUploading] = useState(false);
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [poOpen, setPoOpen] = useState(false);
  const [poSupplierId, setPoSupplierId] = useState("");
  const [newItem, setNewItem] = useState({ part_number: "", manufacturer: "", description: "", quantity: 1, unit_price: 0, currency: "USD", supplier_id: "", lead_time: "", re_reel: false, notes: "" });
  const [orderNumber, setOrderNumber] = useState("");
  const [convertNotes, setConvertNotes] = useState("");
  const fileRef = useRef(null);
  const navigate = useNavigate();

  const load = () => Promise.all([
    api.get(`/offers/${id}`).then((r) => setOffer(r.data)),
    api.get("/suppliers").then((r) => setSuppliers(r.data)),
    api.get(`/offers/${id}/attachments`).then((r) => setAttachments(r.data)).catch(() => setAttachments([])),
  ]);
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post(`/offers/${id}/upload-excel`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Added ${data.added} items from Excel`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const downloadAttachment = async (att) => {
    try {
      const res = await api.get(`/offers/${id}/attachments/${att.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = att.filename || "attachment.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error("Download failed"); }
  };

  const deleteAttachment = async (att) => {
    if (!window.confirm(`Delete "${att.filename}"?`)) return;
    try {
      await api.delete(`/offers/${id}/attachments/${att.id}`);
      toast.success("Attachment removed");
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  const generatePO = async () => {
    if (!poSupplierId) return toast.error("Pick a supplier");
    try {
      const res = await api.get(`/offers/${id}/po.pdf`, {
        params: { supplier_id: poSupplierId },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const supName = (suppliers.find((s) => s.id === poSupplierId)?.name || "supplier").replace(/\s+/g, "_");
      a.href = url; a.download = `PO_${offer?.offer_number || id}_${supName}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("PO PDF generated");
      setPoOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate PO");
    }
  };

  const onDrop = (e) => { e.preventDefault(); if (e.dataTransfer.files?.[0]) upload(e.dataTransfer.files[0]); };
  const onPick = (e) => { if (e.target.files?.[0]) upload(e.target.files[0]); };

  const toggle = (iid) => setSelected((s) => ({ ...s, [iid]: !s[iid] }));
  const toggleAll = () => {
    const all = offer.items.every((it) => selected[it.id]);
    setSelected(all ? {} : Object.fromEntries(offer.items.map((it) => [it.id, true])));
  };

  const updateItem = async (item, patch) => {
    // Optimistic update to keep the UI feeling instant
    setOffer((prev) => prev ? {
      ...prev,
      items: prev.items.map((it) => it.id === item.id ? { ...it, ...patch } : it),
    } : prev);
    try {
      await api.put(`/offers/${id}/items/${item.id}`, patch);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update item");
      load();
    }
  };

  const delItem = async (iid) => {
    await api.delete(`/offers/${id}/items/${iid}`);
    load();
  };

  const addItem = async (e) => {
    e.preventDefault();
    const items = [...(offer.items || []), { ...newItem, supplier_id: newItem.supplier_id || null }];
    try {
      await api.put(`/offers/${id}`, { customer_id: offer.customer_id, title: offer.title, notes: offer.notes, valid_until: offer.valid_until, items });
      // Auto-save new part number to catalog (fire and forget)
      if (newItem.part_number) {
        try {
          const { data: existing } = await api.get("/items", { params: { q: newItem.part_number } });
          const exact = existing.find((x) => x.part_number.toLowerCase() === newItem.part_number.toLowerCase());
          if (!exact) {
            await api.post("/items", {
              part_number: newItem.part_number,
              manufacturer: newItem.manufacturer || null,
              description: newItem.description || null,
            });
          }
        } catch (err) { /* non-blocking catalog auto-save */ console.warn("catalog auto-save failed", err); }
      }
      toast.success("Item added");
      setAddItemOpen(false);
      setNewItem({ part_number: "", manufacturer: "", description: "", quantity: 1, unit_price: 0, currency: "USD", supplier_id: "", lead_time: "", re_reel: false, notes: "" });
      load();
    } catch { toast.error("Failed"); }
  };

  const convert = async (e) => {
    e.preventDefault();
    const ids = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
    if (!ids.length) return toast.error("Select at least one item");
    try {
      const { data } = await api.post("/orders/from-offer", { offer_id: id, item_ids: ids, order_number: orderNumber || null, notes: convertNotes });
      toast.success(`Order ${data.order_number} created`);
      navigate(`/orders/${data.id}`);
    } catch { toast.error("Failed to convert"); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/offers/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Quotation_${offer.offer_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed to generate PDF"); }
  };

  const downloadXlsx = async () => {
    try {
      const res = await api.get(`/offers/${id}/xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Quotation_${offer.offer_number}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed to generate Excel"); }
  };

  const deleteOffer = async () => {
    if (!window.confirm(`Delete offer ${offer.offer_number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/offers/${id}`);
      toast.success("Offer deleted");
      navigate("/offers");
    } catch { toast.error("Failed to delete"); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/offers/template.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "offer_template.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download template"); }
  };

  if (!offer) return <PageBody>Loading…</PageBody>;
  const selCount = Object.values(selected).filter(Boolean).length;

  return (
    <>
      <PageHeader
        title={<span className="font-mono">{offer.offer_number}</span>}
        subtitle={<><Link to="/offers" className="hover:text-blue-700">Offers</Link> · {offer.customer?.name}</>}
        testid="offer-detail-title"
        actions={
          <div className="flex items-center gap-2">
            <StatusBadge status={offer.status} />
            {offer.status !== "converted" && offer.status !== "rejected" && (
              <Button
                onClick={async () => {
                  if (!window.confirm("Mark this offer as REJECTED? The offer is kept but visually crossed out.")) return;
                  try {
                    await api.put(`/offers/${id}/status`, { status: "rejected" });
                    toast.success("Offer marked as rejected");
                    load();
                  } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                }}
                variant="outline"
                className="rounded-none border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
                data-testid="reject-offer-btn"
                title="Mark as rejected instead of deleting"
              >
                Reject
              </Button>
            )}
            {offer.status === "rejected" && (
              <Button
                onClick={async () => {
                  try {
                    await api.put(`/offers/${id}/status`, { status: "open" });
                    toast.success("Offer reopened");
                    load();
                  } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                }}
                variant="outline"
                className="rounded-none border-blue-300 text-blue-700 hover:bg-blue-50 hover:text-blue-800"
                data-testid="reopen-offer-btn"
              >
                Reopen
              </Button>
            )}
            <Button
              onClick={downloadPdf}
              variant="outline"
              className="rounded-none border-slate-300 hover:bg-slate-100"
              data-testid="download-pdf-btn"
            >
              <FileText className="w-4 h-4 mr-2" /> PDF
            </Button>
            <Button
              onClick={downloadXlsx}
              variant="outline"
              className="rounded-none border-slate-300 hover:bg-slate-100"
              data-testid="download-xlsx-btn"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
            </Button>
            <Button
              disabled={!selCount}
              onClick={() => setConvertOpen(true)}
              className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="convert-to-order-btn"
            >
              <ArrowRightCircle className="w-4 h-4 mr-2" /> Convert {selCount ? `${selCount} ` : ""}to Order
            </Button>
            <Button
              onClick={() => { setPoSupplierId(""); setPoOpen(true); }}
              variant="outline"
              className="rounded-none border-indigo-300 text-indigo-800 hover:bg-indigo-50"
              data-testid="create-po-btn"
              title="Create a Purchase Order PDF for one of the primary suppliers on this offer"
            >
              <Send className="w-4 h-4 mr-2" /> Create PO
            </Button>
            <Button
              onClick={deleteOffer}
              variant="outline"
              className="rounded-none border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
              data-testid="delete-offer-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Delete
            </Button>
          </div>
        }
      />
      <PageBody>
        {/* Meta */}
        <div className="grid md:grid-cols-4 border border-slate-300 bg-white mb-6">
          {[
            ["Title", offer.title || "-"],
            ["Valid until", offer.valid_until || "-"],
            ["Items", offer.items?.length || 0],
            ["Created", offer.created_at?.slice(0, 10)],
          ].map(([k, v], i, a) => (
            <div key={k} className={`p-5 ${i < a.length - 1 ? "border-r border-slate-200" : ""}`}>
              <div className="label-caps">{k}</div><div className="font-mono mt-2">{v}</div>
            </div>
          ))}
        </div>

        {/* Uploaded Excel files (supplier quote sheets) */}
        {attachments.length > 0 && (
          <div className="bg-white border border-slate-300 mb-6" data-testid="attachments-section">
            <div className="px-4 py-2.5 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="label-caps text-[10px] text-slate-500">Uploaded supplier quote files</div>
                <div className="text-sm text-slate-800 font-mono">{attachments.length} file{attachments.length === 1 ? "" : "s"} retained</div>
              </div>
              <div className="text-[10px] text-slate-500 font-mono uppercase tracking-wider">Downloadable · never lost</div>
            </div>
            <ul className="divide-y divide-slate-100">
              {attachments.map((att) => (
                <li key={att.id} className="flex items-center gap-3 px-4 py-2" data-testid={`attachment-${att.id}`}>
                  <FileSpreadsheet className="w-4 h-4 text-emerald-700 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-800 truncate font-mono">{att.filename}</div>
                    <div className="text-[11px] text-slate-500">
                      {att.uploaded_at?.slice(0, 16).replace("T", " ")} · {att.uploaded_by || "-"} · {Math.round((att.size || 0) / 1024).toLocaleString()} KB
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        const res = await api.get(`/offers/${id}/attachments/${att.id}`, { responseType: "blob" });
                        const url = URL.createObjectURL(res.data);
                        const a = document.createElement("a");
                        a.href = url; a.download = att.filename || "attachment.xlsx";
                        document.body.appendChild(a); a.click(); a.remove();
                        setTimeout(() => URL.revokeObjectURL(url), 5000);
                      } catch { toast.error("Download failed"); }
                    }}
                    className="text-xs text-blue-700 hover:text-blue-900 inline-flex items-center gap-1"
                    data-testid={`attachment-download-${att.id}`}
                  ><Download className="w-3.5 h-3.5" /> Download</button>
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Delete "${att.filename}"? The line items already parsed from it will remain.`)) return;
                      try {
                        await api.delete(`/offers/${id}/attachments/${att.id}`);
                        toast.success("File removed");
                        load();
                      } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
                    }}
                    className="text-slate-400 hover:text-red-600"
                    data-testid={`attachment-delete-${att.id}`}
                    title="Remove this file (line items stay)"
                  ><Trash2 className="w-3.5 h-3.5" /></button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Upload zone */}
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          className="border-2 border-dashed border-slate-400 bg-slate-50/50 p-8 mb-6 hover:bg-blue-50 hover:border-blue-500 hover:border-solid transition-[background-color,border-color] duration-200 cursor-pointer"
          onClick={() => fileRef.current?.click()}
          data-testid="excel-dropzone"
        >
          <input ref={fileRef} type="file" hidden accept=".xlsx,.xls" onChange={onPick} data-testid="excel-file-input" />
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white border border-slate-300 flex items-center justify-center">
              <Upload className="w-6 h-6 text-slate-600" />
            </div>
            <div>
              <div className="font-display text-lg text-slate-900">{uploading ? "Uploading…" : "Drop Excel file or click to browse"}</div>
              <div className="text-sm text-slate-500">Expected columns: Part Number, Manufacturer, Description, Quantity, Unit Price, Currency, Lead Time, Re-Reel, Notes, then optional Alış Fiyatı 1..5 + Supplier 1..5 pairs. The uploaded file itself is retained above for download.</div>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); downloadTemplate(); }}
                className="text-xs text-blue-700 hover:text-blue-900 inline-flex items-center gap-1"
                data-testid="download-template"
              ><Download className="w-3 h-3" /> template.xlsx</button>
            </div>
          </div>
        </div>

        {/* Uploaded quote files — original Excels retained for reference */}
        {attachments.length > 0 && (
          <div className="border border-slate-300 bg-white mb-6" data-testid="offer-attachments">
            <div className="px-4 py-2 border-b border-slate-200 flex items-center justify-between">
              <div className="label-caps">Uploaded Quote Files ({attachments.length})</div>
              <div className="text-xs text-slate-500">Original supplier-pricing Excels, kept for audit</div>
            </div>
            <ul className="divide-y divide-slate-100">
              {attachments.map((a) => (
                <li key={a.id} className="px-4 py-2 flex items-center gap-3 text-sm" data-testid={`attachment-${a.id}`}>
                  <FileSpreadsheet className="w-4 h-4 text-emerald-700 flex-shrink-0" />
                  <button
                    onClick={() => downloadAttachment(a)}
                    className="font-mono text-blue-700 hover:text-blue-900 truncate"
                    data-testid={`attachment-download-${a.id}`}
                    title="Download the original Excel"
                  >
                    {a.filename}
                  </button>
                  <span className="text-xs text-slate-500 font-mono">
                    {(a.size / 1024).toFixed(1)} KB · {a.uploaded_at?.slice(0, 16).replace("T", " ")} · {a.uploaded_by}
                  </span>
                  <button
                    onClick={() => deleteAttachment(a)}
                    className="ml-auto text-slate-400 hover:text-red-600"
                    data-testid={`attachment-delete-${a.id}`}
                    title="Delete this attachment"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Items */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="label-caps">Line items</div>
            <h2 className="font-display text-2xl tracking-tight text-slate-900">{offer.items?.length || 0} items</h2>
          </div>
          <Dialog open={addItemOpen} onOpenChange={setAddItemOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-none border-slate-300" data-testid="add-item-btn"><Plus className="w-4 h-4 mr-2" /> Add item</Button>
            </DialogTrigger>
            <DialogContent className="rounded-none max-w-lg">
              <DialogHeader><DialogTitle className="font-display tracking-tight">New line item</DialogTitle></DialogHeader>
              <form onSubmit={addItem} className="space-y-3">
                <div>
                  <Label className="label-caps">Part Number *</Label>
                  <div className="mt-1">
                    <PartNumberAutocomplete
                      value={newItem.part_number}
                      onChange={(v) => setNewItem((s) => ({ ...s, part_number: v }))}
                      onPick={(it) => setNewItem((s) => ({
                        ...s,
                        part_number: it.part_number,
                        manufacturer: it.manufacturer || s.manufacturer,
                        description: it.description || s.description,
                      }))}
                      placeholder="Type to search catalog…"
                      testid="new-item-part-number"
                      required
                      autoFocus
                    />
                    <div className="label-caps mt-1 text-slate-400 normal-case tracking-normal text-[10px]">Pick from catalog to auto-fill manufacturer & description.</div>
                  </div>
                </div>
                {[["manufacturer", "Manufacturer"], ["description", "Description"]].map(([k, l]) => (
                  <div key={k}><Label className="label-caps">{l}</Label>
                    <Input className="rounded-none mt-1" value={newItem[k]} onChange={(e) => setNewItem({ ...newItem, [k]: e.target.value })} data-testid={`new-item-${k}`} /></div>
                ))}
                <div className="grid grid-cols-3 gap-3">
                  <div><Label className="label-caps">Qty</Label><Input type="number" step="any" className="rounded-none mt-1" value={newItem.quantity} onChange={(e) => setNewItem({ ...newItem, quantity: parseFloat(e.target.value) })} data-testid="new-item-qty" /></div>
                  <div><Label className="label-caps">Unit price</Label><Input type="number" step="any" className="rounded-none mt-1" value={newItem.unit_price} onChange={(e) => setNewItem({ ...newItem, unit_price: parseFloat(e.target.value) })} data-testid="new-item-price" /></div>
                  <div><Label className="label-caps">Currency</Label>
                    <Select value={newItem.currency} onValueChange={(v) => setNewItem({ ...newItem, currency: v })}>
                      <SelectTrigger className="rounded-none mt-1" data-testid="new-item-currency"><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-none">{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label className="label-caps">Supplier</Label>
                  <Select value={newItem.supplier_id || "none"} onValueChange={(v) => setNewItem({ ...newItem, supplier_id: v === "none" ? "" : v })}>
                    <SelectTrigger className="rounded-none mt-1" data-testid="new-item-supplier"><SelectValue placeholder="Optional" /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="none">— none —</SelectItem>
                      {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-caps">Lead time</Label>
                  <Input className="rounded-none mt-1" placeholder="e.g. 6-8 weeks or In stock" value={newItem.lead_time} onChange={(e) => setNewItem({ ...newItem, lead_time: e.target.value })} data-testid="new-item-lead-time" />
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <Checkbox
                    id="new-item-re-reel"
                    checked={!!newItem.re_reel}
                    onCheckedChange={(v) => setNewItem({ ...newItem, re_reel: !!v })}
                    data-testid="new-item-re-reel"
                  />
                  <Label htmlFor="new-item-re-reel" className="label-caps cursor-pointer">
                    Re-reel <span className="text-slate-400 normal-case tracking-normal">(adds sub-supplier's flat re-reel fee to cost)</span>
                  </Label>
                </div>
                <DialogFooter><Button type="submit" className="rounded-none bg-blue-600 hover:bg-blue-700" data-testid="new-item-save">Add</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {offer.items?.length === 0 ? <EmptyState title="No items" hint="Drag & drop an Excel file above, or add manually." /> :
          <div className="bg-white border border-slate-300 overflow-x-auto">
            <table className="w-full text-sm table-fixed [&_td]:px-2 [&_th]:px-2">
              <colgroup>
                <col style={{ width: "30px" }} />   {/* checkbox */}
                <col style={{ width: "104px" }} />  {/* PART # */}
                <col style={{ width: "88px" }} />   {/* MFR */}
                <col style={{ width: "150px" }} />  {/* DESCRIPTION */}
                <col style={{ width: "68px" }} />   {/* QTY */}
                <col style={{ width: "76px" }} />   {/* UNIT */}
                <col style={{ width: "72px" }} />   {/* CUR */}
                <col style={{ width: "88px" }} />   {/* LEAD TIME */}
                <col style={{ width: "108px" }} />  {/* SUPPLIER */}
                <col style={{ width: "60px" }} />   {/* RE-REEL */}
                <col style={{ width: "84px" }} />   {/* LINE TOTAL */}
                <col style={{ width: "140px" }} />  {/* NOTES */}
                <col style={{ width: "30px" }} />   {/* delete */}
              </colgroup>
              <thead className="bg-slate-50 border-b border-slate-300"><tr className="text-left">
                <th className="py-2 px-2 w-8">
                  <Checkbox
                    data-testid="select-all-items"
                    checked={offer.items.length > 0 && offer.items.every((it) => selected[it.id])}
                    onCheckedChange={toggleAll}
                  />
                </th>
                <th className="py-2 px-3 label-caps">Part #</th>
                <th className="py-2 px-3 label-caps">Mfr</th>
                <th className="py-2 px-3 label-caps">Description</th>
                <th className="py-2 px-3 label-caps text-right">Qty</th>
                <th className="py-2 px-3 label-caps text-right">Unit</th>
                <th className="py-2 px-3 label-caps">Cur</th>
                <th className="py-2 px-3 label-caps">Lead Time</th>
                <th className="py-2 px-3 label-caps">Supplier</th>
                <th className="py-2 px-3 label-caps text-center" title="Re-reel required (adds sub-supplier's flat re-reel fee to cost)">Re-reel</th>
                <th className="py-2 px-3 label-caps text-right">Line total</th>
                <th className="py-2 px-3 label-caps">Notes</th>
                <th className="py-2 px-3 w-8"></th>
              </tr></thead>
              <tbody>
                {offer.items.map((it) => (
                  <tr key={it.id} className="border-b border-slate-200 hover:bg-slate-50" data-testid={`offer-item-row-${it.id}`}>
                    <td className="py-2 px-3">
                      <Checkbox checked={!!selected[it.id]} onCheckedChange={() => toggle(it.id)} data-testid={`select-item-${it.id}`} />
                    </td>
                    <td className="py-2 px-3">
                      <input
                        defaultValue={it.part_number || ""}
                        key={`pn-${it.id}-${it.part_number}`}
                        onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== it.part_number) updateItem(it, { part_number: v }); }}
                        className="w-full h-8 rounded-none border border-slate-300 px-2 text-xs font-mono bg-white"
                        data-testid={`offer-item-pn-${it.id}`}
                      />
                    </td>
                    <td className="py-2 px-3">
                      <input
                        defaultValue={it.manufacturer || ""}
                        key={`mfr-${it.id}-${it.manufacturer || ''}`}
                        onBlur={(e) => { const v = e.target.value.trim(); if (v !== (it.manufacturer || "")) updateItem(it, { manufacturer: v || null }); }}
                        placeholder="—"
                        className="w-full h-8 rounded-none border border-slate-300 px-2 text-xs bg-white"
                        data-testid={`offer-item-mfr-${it.id}`}
                      />
                    </td>
                    <td className="py-2 px-3">
                      <input
                        defaultValue={it.description || ""}
                        key={`d-${it.id}-${it.description || ''}`}
                        onBlur={(e) => { const v = e.target.value.trim(); if (v !== (it.description || "")) updateItem(it, { description: v || null }); }}
                        placeholder="—"
                        className="w-64 h-8 rounded-none border border-slate-300 px-2 text-xs bg-white text-slate-700"
                        data-testid={`offer-item-desc-${it.id}`}
                      />
                    </td>
                    <td className="py-2 px-3 text-right">
                      <input
                        type="number"
                        step="any"
                        defaultValue={it.quantity}
                        key={`q-${it.id}-${it.quantity}`}
                        onBlur={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v) && v !== it.quantity) updateItem(it, { quantity: v }); }}
                        className="w-full h-8 rounded-none border border-slate-300 px-2 text-xs font-mono bg-white text-right"
                        data-testid={`offer-item-qty-${it.id}`}
                      />
                    </td>
                    <td className="py-2 px-3 text-right">
                      <input
                        type="number"
                        step="any"
                        defaultValue={it.unit_price}
                        key={`u-${it.id}-${it.unit_price}`}
                        onBlur={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v) && v !== it.unit_price) updateItem(it, { unit_price: v }); }}
                        className="w-full h-8 rounded-none border border-slate-300 px-2 text-xs font-mono bg-white text-right"
                        data-testid={`offer-item-unit-${it.id}`}
                      />
                    </td>
                    <td className="py-2 px-3">
                      <Select value={it.currency || "USD"} onValueChange={(v) => { if (v !== it.currency) updateItem(it, { currency: v }); }}>
                        <SelectTrigger className="rounded-none h-8 w-20 text-xs font-mono" data-testid={`offer-item-cur-${it.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="rounded-none">
                          {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-2 px-3">
                      <input
                        defaultValue={it.lead_time || ""}
                        key={`lt-${it.id}-${it.lead_time || ''}`}
                        onBlur={(e) => { if (e.target.value !== (it.lead_time || "")) updateItem(it, { lead_time: e.target.value || null }); }}
                        placeholder="—"
                        className="w-full h-8 rounded-none border border-slate-300 px-2 text-xs bg-white"
                        data-testid={`offer-item-lead-time-${it.id}`}
                      />
                    </td>
                    <td className="py-2 px-3">
                      <Select
                        value={it.supplier_id || "none"}
                        onValueChange={(v) => updateItem(it, { supplier_id: v === "none" ? null : v })}
                      >
                        <SelectTrigger className="rounded-none h-8 w-full text-xs" data-testid={`offer-item-supplier-${it.id}`}><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent className="rounded-none">
                          <SelectItem value="none">— none —</SelectItem>
                          {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-2 px-3 text-center">
                      <Checkbox
                        checked={!!it.re_reel}
                        onCheckedChange={(v) => updateItem(it, { re_reel: !!v })}
                        data-testid={`offer-item-re-reel-${it.id}`}
                      />
                    </td>
                    <td className="py-2 px-3 font-mono text-right">{formatMoney(it.quantity * it.unit_price, it.currency)}</td>
                    <td className="py-2 px-3">
                      <input
                        defaultValue={it.notes || ""}
                        key={`nt-${it.id}-${it.notes || ''}`}
                        onBlur={(e) => { if (e.target.value !== (it.notes || "")) updateItem(it, { notes: e.target.value || null }); }}
                        placeholder="—"
                        className="w-full h-8 rounded-none border border-slate-300 px-2 text-xs bg-white"
                        data-testid={`offer-item-notes-${it.id}`}
                      />
                    </td>
                    <td className="py-2 px-3">
                      <button onClick={() => delItem(it.id)} className="text-slate-400 hover:text-red-600" data-testid={`del-item-${it.id}`}><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        }

        {/* Convert dialog */}
        <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
          <DialogContent className="rounded-none max-w-md">
            <DialogHeader><DialogTitle className="font-display tracking-tight">Convert to Order</DialogTitle></DialogHeader>
            <form onSubmit={convert} className="space-y-3">
              <div className="text-sm text-slate-600">Creating an order from <span className="font-mono">{selCount}</span> selected item(s).</div>
              <div><Label className="label-caps">Order number (optional)</Label>
                <Input className="rounded-none mt-1" value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} placeholder="Auto-generated if empty" data-testid="convert-order-number" /></div>
              <div><Label className="label-caps">Notes</Label>
                <Input className="rounded-none mt-1" value={convertNotes} onChange={(e) => setConvertNotes(e.target.value)} data-testid="convert-notes" /></div>
              <DialogFooter><Button type="submit" className="rounded-none bg-emerald-600 hover:bg-emerald-700" data-testid="convert-confirm-btn">Create Order</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* PO dialog */}
        <Dialog open={poOpen} onOpenChange={setPoOpen}>
          <DialogContent className="rounded-none max-w-lg" data-testid="po-dialog">
            <DialogHeader>
              <DialogTitle className="font-display tracking-tight flex items-center gap-2">
                <Send className="w-5 h-5 text-indigo-700" /> Create Purchase Order
              </DialogTitle>
            </DialogHeader>
            {(() => {
              // Only suppliers that are set as primary on at least one item are eligible
              const supCounts = {};
              for (const it of (offer?.items || [])) {
                if (it.supplier_id && it.purchase_price != null) {
                  supCounts[it.supplier_id] = (supCounts[it.supplier_id] || 0) + 1;
                }
              }
              const eligible = suppliers.filter((s) => supCounts[s.id]);
              if (eligible.length === 0) {
                return (
                  <div className="text-sm text-slate-600 p-4 bg-amber-50 border border-amber-200">
                    None of the items on this offer have a primary supplier and purchase price set.
                    Fill the <span className="font-mono">Primary Supplier</span> + matching <span className="font-mono">Alış Fiyatı N</span>/<span className="font-mono">Supplier N</span> columns in the template and re-upload,
                    or set them per-line in the items table below.
                  </div>
                );
              }
              return (
                <div className="space-y-4">
                  <div>
                    <Label className="label-caps">Supplier</Label>
                    <Select value={poSupplierId} onValueChange={setPoSupplierId}>
                      <SelectTrigger className="rounded-none mt-1" data-testid="po-supplier-select">
                        <SelectValue placeholder="Select a supplier…" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {eligible.map((s) => (
                          <SelectItem key={s.id} value={s.id} data-testid={`po-supplier-${s.id}`}>
                            {s.name} <span className="text-slate-500">· {supCounts[s.id]} item{supCounts[s.id] === 1 ? "" : "s"}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 space-y-1">
                    <div><strong>PO contents:</strong></div>
                    <div>· Only line items where selected supplier is set as primary</div>
                    <div>· Uses each item's <span className="font-mono">purchase price</span>, not the customer sale price</div>
                    <div>· PO number = <span className="font-mono">PO-{offer?.offer_number || "…"}</span></div>
                  </div>
                </div>
              );
            })()}
            <DialogFooter>
              <Button variant="outline" onClick={() => setPoOpen(false)} className="rounded-none" data-testid="po-cancel">Cancel</Button>
              <Button
                onClick={generatePO}
                disabled={!poSupplierId}
                className="rounded-none bg-indigo-700 hover:bg-indigo-800 text-white"
                data-testid="po-generate"
              >
                <Send className="w-4 h-4 mr-2" /> Generate PDF
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageBody>
    </>
  );
}
